import authFactory from "@/auth/services/authFactory";

export const safeJSONParse = <T = unknown>(input: string, fallback: T = {} as T): T => {
  try {
    return JSON.parse(input);
  } catch {
    return fallback;
  }
};

export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      localStorage?.setItem(key, value);
    } catch {}
  },

  removeItem: (key: string): void => {
    try {
      localStorage?.removeItem(key);
    } catch {}
  },

  clear: (): void => {
    try {
      localStorage?.clear();
    } catch {}
  },
};

export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    try {
      return sessionStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  setItem: (key: string, value: string): void => {
    try {
      sessionStorage?.setItem(key, value);
    } catch {}
  },

  removeItem: (key: string): void => {
    try {
      sessionStorage?.removeItem(key);
    } catch {}
  },

  clear: (): void => {
    try {
      sessionStorage?.clear();
    } catch {}
  },
};

export const getUserDataFromLocalStorage = () => {
  const userString = safeLocalStorage.getItem("user") || "";

  const session = authFactory.getAuthConfig()?.payload?.session;
  const userData = userString
    ? safeJSONParse<{
        token: string | null;
        isLoggedIn: boolean;
        getCurrentUser: any;
        session: string;
      }>(userString)
    : null;

  if (userData?.token && userData?.session === session) {
    return userData;
  } else {
    if (userData) {
      safeLocalStorage.removeItem("user");
    }
    return null;
  }
};

/**
 * Triggers haptic feedback/vibration on supported devices
 * Works on: Android browsers, Telegram WebApp (including iOS)
 * Does not work on: iOS Safari, iOS Chrome/Firefox (Apple limitation)
 *
 * @param pattern - Vibration pattern: single number (ms) or array of [vibrate, pause, vibrate, ...]
 * @example
 * vibrate(50); // Single 50ms vibration
 * vibrate([100, 50, 100]); // Vibrate 100ms, pause 50ms, vibrate 100ms
 */
export const vibrate = (pattern: number | number[]): void => {
  // Try Telegram WebApp API first (works on iOS in Telegram)
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp?.HapticFeedback) {
    (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
    return;
  }

  // Fallback to standard Vibration API (Android, some browsers)
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};
